import React, { useCallback, useMemo, useState } from "react";

import useGetHouseholds from "@fartherfinance/frontend/api/Households/hooks/useGetHouseholds";

import { HouseholdOrderBy, HousholdTableProps } from "../types";
import { useAdvisorPageInit } from "@src/multiCustodian/hooks/Advisor/useAdvisorPageInit";
import { useTableRows } from "@src/multiCustodian/hooks/Advisor/useTableRows";
import PaginatedTable from "@src/sharedComponents/Table/PaginatedTable";
import { Column, TableSortOrder } from "@src/sharedComponents/Table/Types";

import {
  createDisplayDataRow,
  householdTableColumnDefs,
  TableKeys,
  TableKeysType,
} from "./columnDefs";

const getSortField = (column: TableKeysType): HouseholdOrderBy => {
  switch (column) {
    case "Household":
      return "name";
    case "Date Established":
      return "createdAt";
    default:
      return "createdAt";
  }
};

const HouseholdsTable: React.FC<HousholdTableProps> = ({ search }) => {
  const { history, auth, advisorId } = useAdvisorPageInit();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [sortColumn, setSortColumn] =
    useState<TableKeysType>("Date Established");
  const [sortOrder, setSortOrder] = useState<TableSortOrder>("desc");

  const { data, isLoading, hasError } = useGetHouseholds(
    {
      page: currentPage,
      pageSize,
      orderBy: `${getSortField(sortColumn)} ${sortOrder}`,
      advisorId: advisorId!,
      search: search,
    },
    auth
  );

  const openHouseholdDetails = useCallback(
    (householdId: string) => {
      const location = history.location;
      history.push({
        ...location,
        pathname: `/Advisor/Households/${householdId}`,
      });
    },
    [history]
  );

  const columns = useMemo((): Column<TableKeysType>[] => {
    return TableKeys.map((key) => {
      const isSortable = ["Household", "Date Established"].includes(key);
      return isSortable
        ? {
            label: key,
            accessor: key,
            sortable: true as const,
            active: key === sortColumn,
            sortOrder: key === sortColumn ? sortOrder : "asc",
          }
        : {
            label: key,
            accessor: key,
            sortable: false as const,
          };
    });
  }, [sortColumn, sortOrder]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleColumnSelect = useCallback(
    (column: TableKeysType, order: TableSortOrder) => {
      setSortColumn(column);
      setSortOrder(order);
      setCurrentPage(1); // Reset to first page when sorting changes
    },
    []
  );

  const pagination = useMemo(() => {
    if (data?.pagination === undefined) {
      return {
        currentPage,
        totalPages: 1,
        pageSize,
        totalCount: 0,
        prevPage: undefined,
        nextPage: undefined,
      };
    }

    const totalPages = Math.ceil(
      data.pagination.totalCount / data.pagination.pageSize
    );

    return {
      currentPage,
      totalPages,
      pageSize: data.pagination.pageSize,
      totalCount: data.pagination.totalCount,
      prevPage: currentPage === 1 ? undefined : currentPage - 1,
      nextPage: currentPage === totalPages ? undefined : currentPage + 1,
    };
  }, [data, currentPage, pageSize]);

  const tableRowData = useMemo(() => {
    if (hasError || data?.data === undefined) {
      return [];
    }
    return data.data.map((household) =>
      createDisplayDataRow(household, openHouseholdDetails)
    );
  }, [data?.data, openHouseholdDetails, hasError]);

  const rows = useTableRows({
    searchedData: tableRowData,
    columnDefinitions: householdTableColumnDefs,
    isLoading: isLoading,
  });

  return (
    <PaginatedTable
      isLoading={isLoading}
      columns={columns}
      rows={rows}
      pagination={pagination}
      onPageChange={handlePageChange}
      onColumnSelect={handleColumnSelect}
      showEmptyRows={false}
    />
  );
};

export default HouseholdsTable;
